import { useQuery } from '@tanstack/react-query';
import { Icoon } from '../ui/Icoon';
import { useState } from 'react';
import { Link } from 'react-router-dom';
import { fmtDag, werkdagenLijst } from '@ep/core';
import { api } from '../lib/api';
import { NIVEAUS, useActie, useData, type EngineerRij } from '../lib/data';
import { AdresVeld, type GeoWaarde } from '../ui/AdresVeld';
import { getal, Laden, Modal, TIJDEN, Veld, VerwijderKnop } from '../ui/ui';
import { AgendaStatusBadge, type Aanbieders, type KoppelingInfo } from './MijnAgenda';

interface AgendaOverzicht { organisatie: boolean; aanbieders: Aanbieders; koppelingen: (KoppelingInfo & { engineerId: string })[] }
const useAgendaOverzicht = () => useQuery({ queryKey: ['agenda-koppelingen'], queryFn: () => api.get<AgendaOverzicht>('/api/beheer/agenda-koppelingen') });

const DAGEN = ['ma', 'di', 'wo', 'do', 'vr', 'za', 'zo'];

export function Beheer() {
  const { data: d } = useData();
  const [eng, setEng] = useState<EngineerRij | 'nieuw' | null>(null);
  const [nieuweExp, setNieuweExp] = useState('');
  const [fd, setFd] = useState({ naam: '', datum: '', heleDag: true, vanTijd: '12:00', totTijd: '17:00' });
  const [toonInactief, setToonInactief] = useState(false);
  const ag = useAgendaOverzicht().data;
  const expToevoegen = useActie(() => api.post('/api/expertises', { naam: nieuweExp }), { succes: 'Expertise toegevoegd', opNa: () => setNieuweExp('') });
  const expVerwijder = useActie((id: string) => api.del(`/api/expertises/${id}`), { succes: 'Expertise verwijderd' });
  const fdToevoegen = useActie(() => api.post('/api/feestdagen', { naam: fd.naam, datum: fd.datum, vanTijd: fd.heleDag ? null : fd.vanTijd, totTijd: fd.heleDag ? null : fd.totTijd }), {
    succes: 'Vrije dag toegevoegd; de agenda\'s worden bijgewerkt',
    opNa: () => setFd({ ...fd, naam: '', datum: '' }),
  });
  const fdVerwijder = useActie((id: string) => api.del(`/api/feestdagen/${id}`), { succes: 'Vrije dag verwijderd' });
  if (!d) return <Laden />;
  const lijst = d.engineers.filter((e) => toonInactief || e.actief !== false);
  return (
    <div className="pagina" style={{ display: 'grid', gridTemplateColumns: '1.6fr 1fr', gap: 16, alignItems: 'start' }}>
      <div className="paneel">
        <div className="sectie-kop">
          <h1 className="groei">Engineers</h1>
          <label className="check"><input type="checkbox" checked={toonInactief} onChange={(e) => setToonInactief(e.target.checked)} /> Inactieve tonen</label>
          <Link className="knop" to="/beheer/gebruikers"><Icoon naam="users" maat={16} /> Gebruikers</Link>
          <Link className="knop" to="/beheer/instellingen"><Icoon naam="settings" maat={16} /> Instellingen</Link>
          <button className="knop primair" onClick={() => setEng('nieuw')}>+ Nieuw</button>
        </div>
        <table className="tabel">
          <thead><tr><th>Naam</th><th>Expertises</th><th>Werkdagen</th><th className="rechts">Uren/dag</th><th>Vertrek</th><th>Agenda</th><th /></tr></thead>
          <tbody>
            {lijst.map((e) => (
              <tr key={e.id} className="klik" onClick={() => setEng(e)} style={{ opacity: e.actief === false ? 0.5 : 1 }}>
                <td><b>{e.naam}</b><div className="klein mute">{e.email}</div></td>
                <td className="klein">{e.expertises.map((x) => `${x.naam} ${x.niveau}%`).join(', ')}</td>
                <td>{werkdagenLijst(e.werkdagen).join(', ')}</td>
                <td className="rechts">{e.urenPerDag}</td>
                <td className="klein">{e.startlocatie === 'KANTOOR' ? '🏢 kantoor' : e.lat != null ? `🏠 ${e.geoNaam ?? e.adres}` : <span style={{ color: 'var(--rood)' }}>⚠ geen adres</span>}{e.inzet !== 'VOLLEDIG' && <div className="mute">{e.inzet === 'NIET_SUGGESTIE' ? 'niet in suggestie' : 'niet in capaciteit'}</div>}</td>
                <td>{ag && <AgendaStatusBadge k={ag.koppelingen.find((k) => k.engineerId === e.id)} organisatie={ag.organisatie} />}</td>
                <td className="mute"><Icoon naam="pencil" maat={16} /></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <div className="kolom" style={{ gap: 16 }}>
        <div className="paneel kolom">
          <h2>Expertises</h2>
          <div className="rij">
            <input value={nieuweExp} onChange={(e) => setNieuweExp(e.target.value)} placeholder="Nieuwe expertise" onKeyDown={(e) => e.key === 'Enter' && nieuweExp && expToevoegen.mutate()} />
            <button className="knop primair" disabled={!nieuweExp} onClick={() => expToevoegen.mutate()}>Toevoegen</button>
          </div>
          <table className="tabel">
            <tbody>
              {d.expertises.map((x) => (
                <tr key={x.id}>
                  <td>{x.naam}</td>
                  <td className="klein mute">{d.engineers.filter((e) => e.expertises.some((y) => y.naam === x.naam)).length} engineers · {d.projecten.filter((p) => p.expertiseBudget.some((b) => b.naam === x.naam)).length} projecten</td>
                  <td className="rechts"><VerwijderKnop klein label="🗑" onBevestig={() => expVerwijder.mutate(x.id)} /></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <div className="paneel kolom">
          <h2>Feestdagen en vrije dagen</h2>
          <div className="grid2">
            <Veld label="Naam"><input value={fd.naam} onChange={(e) => setFd({ ...fd, naam: e.target.value })} /></Veld>
            <Veld label="Datum"><input type="date" value={fd.datum} onChange={(e) => setFd({ ...fd, datum: e.target.value })} /></Veld>
            <label className="check span2"><input type="checkbox" checked={fd.heleDag} onChange={(e) => setFd({ ...fd, heleDag: e.target.checked })} /> Hele dag (niemand wordt ingepland)</label>
            {!fd.heleDag && (
              <>
                <Veld label="Van"><select value={fd.vanTijd} onChange={(e) => setFd({ ...fd, vanTijd: e.target.value })}>{TIJDEN.map((t) => <option key={t}>{t}</option>)}</select></Veld>
                <Veld label="Tot"><select value={fd.totTijd} onChange={(e) => setFd({ ...fd, totTijd: e.target.value })}>{TIJDEN.map((t) => <option key={t}>{t}</option>)}</select></Veld>
              </>
            )}
          </div>
          <button className="knop primair" disabled={!fd.naam || !fd.datum} onClick={() => fdToevoegen.mutate()}>Toevoegen</button>
          <table className="tabel">
            <tbody>
              {[...d.feestdagen].sort((a, b) => a.datum.localeCompare(b.datum)).filter((f) => f.datum >= d.vandaag.slice(0, 4)).map((f) => (
                <tr key={f.id}>
                  <td>{fmtDag(f.datum, 'jaar')}</td>
                  <td>{f.naam}</td>
                  <td className="klein">{f.vanTijd ? `${f.vanTijd}–${f.totTijd}` : 'hele dag'}</td>
                  <td className="rechts"><VerwijderKnop klein label="🗑" onBevestig={() => fdVerwijder.mutate(f.id)} /></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
      {eng && <EngineerForm e={eng === 'nieuw' ? undefined : eng} onSluit={() => setEng(null)} />}
    </div>
  );
}

function EngineerForm({ e, onSluit }: { e?: EngineerRij; onSluit: () => void }) {
  const { data: d } = useData();
  const cfg = d!.instellingen;
  const [f, setF] = useState({
    naam: e?.naam ?? '',
    email: e?.email ?? '',
    functie: e?.functie ?? '',
    werkdagen: werkdagenLijst(e?.werkdagen ?? cfg.engDagen) as string[],
    urenPerDag: String(e?.urenPerDag ?? cfg.engUren),
    inzet: e?.inzet ?? 'VOLLEDIG',
    startlocatie: e?.startlocatie ?? 'THUIS',
    actief: e?.actief !== false,
    expertises: e?.expertises.map((x) => ({ expertiseId: x.expertiseId, niveau: x.niveau })) ?? [],
  });
  const [geo, setGeo] = useState<GeoWaarde>({ adres: e?.adres ?? '', lat: e?.lat ?? null, lon: e?.lon ?? null, naam: e?.geoNaam ?? null, gewijzigd: false });
  const opslaan = useActie(
    () => {
      const body = {
        ...f,
        werkdagen: DAGEN.filter((x) => f.werkdagen.includes(x)).join(','),
        urenPerDag: getal(f.urenPerDag) ?? 8,
        adres: geo.adres || null,
        ...(geo.gewijzigd && geo.lat != null ? { lat: geo.lat, lon: geo.lon, geoAdres: geo.adres, geoNaam: geo.naam } : e && !geo.gewijzigd ? { lat: e.lat, lon: e.lon, geoAdres: e.geoAdres, geoNaam: e.geoNaam } : {}),
      };
      return e ? api.put(`/api/engineers/${e.id}`, body) : api.post('/api/engineers', body);
    },
    { succes: 'Engineer opgeslagen', opNa: onSluit },
  );
  const verwijder = useActie(() => api.del<{ melding?: string }>(`/api/engineers/${e!.id}`), { succes: (r) => r.melding ?? 'Engineer verwijderd', opNa: onSluit });
  return (
    <Modal
      titel={e ? 'Engineer bewerken' : 'Nieuwe engineer'}
      onSluit={onSluit}
      breedte="breed"
      voet={
        <>
          {e && <VerwijderKnop onBevestig={() => verwijder.mutate()} />}
          <div className="groei" />
          <button className="knop" onClick={onSluit}>Annuleren</button>
          <button className="knop primair" onClick={() => opslaan.mutate()}>Opslaan</button>
        </>
      }
    >
      <div className="grid3">
        <Veld label="Naam *"><input value={f.naam} onChange={(x) => setF({ ...f, naam: x.target.value })} /></Veld>
        <Veld label="E-mailadres *" hint="Voor mails en de agenda; een eigen agenda mag een ander adres hebben"><input value={f.email} onChange={(x) => setF({ ...f, email: x.target.value })} /></Veld>
        <Veld label="Functie / omschrijving"><input value={f.functie} onChange={(x) => setF({ ...f, functie: x.target.value })} /></Veld>
        <Veld label="Werkdagen">
          <div className="rij wrap">
            {DAGEN.map((x) => (
              <label className="check" key={x}><input type="checkbox" checked={f.werkdagen.includes(x)} onChange={(ev) => setF({ ...f, werkdagen: ev.target.checked ? [...f.werkdagen, x] : f.werkdagen.filter((y) => y !== x) })} />{x}</label>
            ))}
          </div>
        </Veld>
        <Veld label="Uren per dag"><input value={f.urenPerDag} onChange={(x) => setF({ ...f, urenPerDag: x.target.value })} /></Veld>
        <Veld label="Inzet">
          <select value={f.inzet} onChange={(x) => setF({ ...f, inzet: x.target.value as typeof f.inzet })}>
            <option value="VOLLEDIG">Volledig meetellen</option>
            <option value="NIET_SUGGESTIE">Niet in slimme suggestie</option>
            <option value="NIET_CAPACITEIT">Niet in suggestie en niet in capaciteit</option>
          </select>
        </Veld>
        <div className="span2"><AdresVeld label="Vertrekadres (thuis)" waarde={geo} onChange={setGeo} /></div>
        <Veld label="Vertrek vanaf">
          <select value={f.startlocatie} onChange={(x) => setF({ ...f, startlocatie: x.target.value as typeof f.startlocatie })}>
            <option value="THUIS">Thuis</option>
            <option value="KANTOOR">Kantoor{cfg.kantoorAdres ? ` (${cfg.kantoorAdres})` : ''}</option>
          </select>
        </Veld>
        <label className="check"><input type="checkbox" checked={f.actief} onChange={(x) => setF({ ...f, actief: x.target.checked })} /> Actief</label>
      </div>
      {e && <AgendaBeheer engineerId={e.id} email={e.email} />}
      <h3 style={{ margin: '14px 0 8px' }}>Expertise en kennisniveau</h3>
      <div className="grid2">
        {d!.expertises.map((x) => {
          const huidig = f.expertises.find((y) => y.expertiseId === x.id);
          return (
            <div className="rij" key={x.id}>
              <label className="check groei"><input type="checkbox" checked={!!huidig} onChange={(ev) => setF({ ...f, expertises: ev.target.checked ? [...f.expertises, { expertiseId: x.id, niveau: 100 }] : f.expertises.filter((y) => y.expertiseId !== x.id) })} />{x.naam}</label>
              {huidig && (
                <select value={huidig.niveau} style={{ width: 210 }} onChange={(ev) => setF({ ...f, expertises: f.expertises.map((y) => (y.expertiseId === x.id ? { ...y, niveau: Number(ev.target.value) } : y)) })}>
                  {NIVEAUS.map((n) => <option key={n.p} value={n.p}>{n.label}</option>)}
                </select>
              )}
            </div>
          );
        })}
      </div>
    </Modal>
  );
}

/** Agenda van een engineer: status, koppellink sturen, ontkoppelen (SPEC §8.3). */
function AgendaBeheer({ engineerId, email }: { engineerId: string; email: string }) {
  const q = useAgendaOverzicht();
  const [link, setLink] = useState<{ link: string; gemaild: boolean } | null>(null);
  const stuur = useActie(() => api.post<{ link: string; gemaild: boolean }>(`/api/engineers/${engineerId}/koppellink`), {
    succes: (r) => (r.gemaild ? `Koppellink gemaild naar ${email}` : 'Koppellink aangemaakt'),
    opNa: (r) => setLink(r),
  });
  const ontkoppel = useActie(() => api.del(`/api/engineers/${engineerId}/agenda`), { succes: 'Agenda ontkoppeld', opNa: () => q.refetch() });
  if (!q.data) return null;
  const k = q.data.koppelingen.find((x) => x.engineerId === engineerId);
  return (
    <>
      <h3 style={{ margin: '14px 0 8px' }}>Agenda</h3>
      <div className="kolom" style={{ gap: 8 }}>
        <div className="rij wrap">
          <AgendaStatusBadge k={k} organisatie={q.data.organisatie} />
          {k && <span className="klein mute">{k.account}</span>}
          <div className="groei" />
          <button className="knop klein" onClick={() => stuur.mutate()}><Icoon naam="mail" maat={14} /> Koppellink sturen</button>
          {k && <VerwijderKnop label="Ontkoppelen" klein onBevestig={() => ontkoppel.mutate()} />}
        </div>
        {k?.status === 'OPNIEUW' && <div className="klein" style={{ color: 'var(--rood)' }}>{k.melding} – stuur een koppellink zodat de engineer opnieuw kan koppelen.</div>}
        {link && (
          <div className="paneel vlak klein">
            {link.gemaild ? 'De engineer krijgt een mail (in testmodus staat die in het testpostvak). ' : 'De mail kon niet verstuurd worden. '}Je kunt de link ook zelf delen; hij is 7 dagen geldig en werkt één keer:
            <div className="rij" style={{ marginTop: 6 }}>
              <input readOnly value={link.link} onFocus={(ev) => ev.target.select()} />
              <button className="knop klein" onClick={() => navigator.clipboard.writeText(link.link)}>Kopiëren</button>
            </div>
          </div>
        )}
      </div>
    </>
  );
}

